import React from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, Typography, Alert } from '@mui/material';
import { formatNumber } from '@vortx/shared-ui';

function getLoadSeries(component = {}, variants = []) {
  for (const key of variants) {
    if (Array.isArray(component[key])) {
      return component[key];
    }
  }
  return [];
}

export function calculateUnmetLoadMetrics(results = {}) {
  const outputs = results.outputs || {};
  const electric_load = outputs.ElectricLoad || {};
  
  // Get total load and check if there's unmet load data
  const load_series = electric_load.load_series_kw || [];
  const annual_kwh = electric_load.annual_kwh;
  const total_load_kwh = annual_kwh != null
    ? annual_kwh
    : load_series.reduce((sum, val) => sum + (val || 0), 0);
  
  // Use REopt's official off-grid load coverage data
  
  const critical = getLoadSeries(electric_load, [
    "critical_load_series_kw", "load_series_kw"
  ]);
  const offgrid_met = electric_load.offgrid_load_met_series_kw || [];
  
  let total_unmet_kwh = 0;
  let unmet_hours = 0;
  let max_unmet_kw = 0;
  const hourly_unmet = [];
  
  for (let i = 0; i < Math.max(critical.length, offgrid_met.length); i++) {
    const unmet = Math.max(0, (critical[i] ?? 0) - (offgrid_met[i] ?? 0));
    hourly_unmet.push(unmet);
    
    if (unmet > 0.01) { // Ignore tiny rounding errors
      total_unmet_kwh += unmet;
      unmet_hours += 1;
      max_unmet_kw = Math.max(max_unmet_kw, unmet);
    }
  }
  
  // Calculate percentages
  const unmet_percentage = total_load_kwh > 0 ? (total_unmet_kwh / total_load_kwh) * 100 : 0;
  const load_met_percentage = 100 - unmet_percentage;
  
  // Check REopt's official load met fraction if available
  const official_load_met_fraction = outputs.offgrid_load_met_fraction;
  
  return {
    total_load_kwh,
    total_unmet_kwh,
    unmet_percentage,
    load_met_percentage,
    unmet_hours,
    max_unmet_kw,
    total_hours: Math.max(critical.length, offgrid_met.length),
    official_load_met_fraction,
    has_unmet_load: total_unmet_kwh > 0.01
  };
}

export default function UnmetLoadTable({ results, isOffGrid = false }) {
  if (!results) return null;
  
  const metrics = calculateUnmetLoadMetrics(results);
  
  // Only show this table if there's unmet load or it's an off-grid scenario
  if (!metrics.has_unmet_load && !isOffGrid) return null;
  
  const rows = [
    { label: 'Total Load', value: formatNumber(metrics.total_load_kwh, 0), unit: 'kWh' },
    { label: 'Unmet Load', value: formatNumber(metrics.total_unmet_kwh, 0), unit: 'kWh' },
    { label: 'Unmet Load Percentage', value: formatNumber(metrics.unmet_percentage, 2), unit: '%' },
    { label: 'Load Met Percentage', value: formatNumber(metrics.load_met_percentage, 2), unit: '%' },
    { label: 'Unmet Hours', value: formatNumber(metrics.unmet_hours, 0), unit: `of ${metrics.total_hours}` },
    { label: 'Peak Unmet Load', value: formatNumber(metrics.max_unmet_kw, 1), unit: 'kW' },
  ];
  
  // Add official REopt metric if available
  if (metrics.official_load_met_fraction !== null && metrics.official_load_met_fraction !== undefined) {
    rows.splice(4, 0, {
      label: 'REopt Load Met Fraction',
      value: formatNumber(metrics.official_load_met_fraction * 100, 2),
      unit: '%'
    });
  }
  
  return (
    <>
      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Load Coverage Analysis
      </Typography>
      
      {metrics.has_unmet_load && (
        <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
          ⚠️ System cannot meet {formatNumber(metrics.unmet_percentage, 1)}% of load demand
        </Alert>
      )}
      
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Metric</TableCell>
            <TableCell align="right">Value</TableCell>
            <TableCell align="right">Unit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow 
              key={row.label} 
              sx={{ 
                backgroundColor: idx % 2 ? 'action.hover' : 'inherit',
                ...(row.label === 'Unmet Load' && metrics.has_unmet_load ? 
                  { backgroundColor: 'warning.light', fontWeight: 'bold' } : {})
              }}
            >
              <TableCell>{row.label}</TableCell>
              <TableCell align="right">{row.value}</TableCell>
              <TableCell align="right">{row.unit}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {metrics.has_unmet_load && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Consider increasing PV capacity, battery storage, or generator size to eliminate unmet load.
        </Typography>
      )}
    </>
  );
}
